import React, { useState } from "react";

import { API } from "aws-amplify";

import { performUpdateUserReloadCarrier } from "../../../../graphql/carrier/carrierCRUD";

import { Button, Confirm, useNotify, useRedirect } from "react-admin";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { DeleteIcon } from "../../../../components/common/iconography";
import { logError } from "../../../../utils/appUtils";

import { isCarrierAdmin } from "../../../../utils/carrierUserUtils";

import { sendEmails } from "./carrierUtils";
import { emailDeleteHTML } from "./emailDeleteHTML";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

/**
 * Delete a user from this transload.
 *
 * For a driver this is one action with one confirmation, and the server decides
 * what it means — whether the record can go with the membership is a question
 * about other tenants' junction rows and check-in history, and AppSync shows a
 * manager neither (junctions are group-scoped, so a driver working three
 * transloads looks exactly like a driver working one). The manager asked for the
 * same thing either way, so the outcome is reported rather than chosen; there is
 * nothing here to gate, and no preflight, because the delete never refuses.
 *
 * Deleting a standing driver's personal data is deliberately not on offer. A
 * driver ID travels with the person between transloads, and withdrawing
 * biometric consent is theirs to do at any kiosk, face-verified ("Delete my
 * driver ID"). Their name stays on their check-ins either way — an audit trail
 * naming nobody is not an audit trail.
 */
export const DeepDeleteButton = ({ record, className, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isDriver = record?.userType === "driver";

  // The carrier's own address is its primary account — the one the carrierApp
  // will not let colleagues delete, and the one every carrier is created with.
  // Revoking it here is still the yard's power (access at this yard is the
  // yard's to withdraw), but it leaves the company with no dispatch login and
  // no carrier-side way back in — only Send Invite on the carrier — so the
  // dialog says exactly that instead of the ordinary wording.
  const isPrimary = isCarrierAdmin(record);

  const driverName =
    [record?.firstName, record?.lastName].filter(Boolean).join(" ") ||
    "this driver";

  const handleDialogClose = () => {
    if (isDeleting) return;
    setOpen(false);
  };

  // The manager's own confirmation copy, sent for a revoke and only a revoke:
  // an erased record has no access left to report the withdrawal of, and that
  // is how this has always behaved.
  const notifyManagerByEmail = () =>
    sendEmails(
      {
        email: window.appUser.email,
        firstName: window.appUser.firstName,
        lastName: window.appUser.lastName,
        reloadYardName: window.appUser.reloadYard.name,
      },
      emailDeleteHTML,
    );

  // Dispatch users stay on the client path: their junction is the only thing a
  // delete has ever touched, the row is this yard's to write, and none of the
  // driver questions (other tenants, check-in history, a portable driver ID)
  // apply to them.
  const handleRevoke = async () => {
    setIsDeleting(true);
    try {
      // deleted=true distinguishes explicit deletes from archived-with-carrier
      // so ArchiveRestoreButton.handleRestore does not resurrect deleted users
      const reloadYardCarrier = {
        ...record?.reloadYardCarriers?.items[0],
        active: "false",
        deleted: true,
      };

      await performUpdateUserReloadCarrier(reloadYardCarrier);

      // send silently don't await
      notifyManagerByEmail();

      notify("User deleted successfully", "success");
      redirect("/carrierUsers");
    } catch (error) {
      logError(
        "managerApp:carriers:DeepDeleteButton:handleDelete",
        error,
        notify,
      );
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  const handleDeleteDriver = async () => {
    setIsDeleting(true);
    try {
      const result = await API.post("transREST", "/driverAdmin/deleteDriver", {
        body: { carrierUserId: record.id },
      });

      if (result?.mode === "revoked") {
        // send silently don't await
        notifyManagerByEmail();
        notify("Driver access revoked", "success");
      } else {
        notify("Driver deleted — they had no check-ins to keep.", "success");
      }
      setOpen(false);
      redirect("/carrierUsers");
    } catch (error) {
      logError(
        "managerApp:carriers:DeepDeleteButton:handleDeleteDriver",
        error,
        notify,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button
        {...props}
        label="Delete"
        disabled={isDeleting}
        onClick={() => setOpen(true)}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title="Delete Confirmation"
        content={
          isDriver
            ? `Remove ${driverName} from your transload? Their access here ends ` +
              "immediately. If no other transload has them on file and they " +
              "have no check-in history, their record is deleted outright; " +
              "otherwise the record stays, name included, for the check-in " +
              "audit trail. Their driver ID — face and licence — is theirs to " +
              "delete at any gate kiosk."
            : isPrimary
              ? "This is the carrier's primary account. Deleting it leaves " +
                "the company with no dispatch login here until you re-invite " +
                "them with Send Invite on the carrier. Delete anyway?"
              : "Delete this user's access to your transload? If it is the " +
                "last one they work with, their sign-in is deleted too and a " +
                "future invitation starts them at registration."
        }
        onConfirm={isDriver ? handleDeleteDriver : handleRevoke}
        onClose={handleDialogClose}
      />
    </>
  );
};
